import { useEffect, useState, useMemo } from "react";
import { PlayerResult } from "@/types";
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Spinner, Pagination } from "@heroui/react";
import { Link } from 'react-router-dom';
import PlayerLogo from "../player-logo";
import TeamLogo from "../team-logo";
import { isMobile } from "@/stores/MenuStore";
import { Icon } from "@iconify/react";
import { Tooltip } from "@heroui/tooltip";
import { useTranslation } from 'react-i18next';

interface FinderResultProps {
    tableResult: PlayerResult[];
    leaguesNames: string[] | undefined;
    statName: string | undefined;
    atLeast: string;
    lastGames: string;
    hitRate: number;
    seeResult: boolean;
    loading: boolean;
}

const formatNumber = (value: any): string => {
    if (typeof value === 'number') {
        return value % 1 === 0 ? value.toString() : value.toFixed(1);
    }
    return value;
};

const FinderResult = function FinderResult({ tableResult, leaguesNames, statName, atLeast, lastGames, hitRate, seeResult, loading }: FinderResultProps) {
    const { t } = useTranslation();
    const columns = [
        { key: "player_name", label: t("finder_result.finder_result_1") },
        { key: "next_team", label: t("finder_result.finder_result_2") },
        { key: "total", label: t("finder_result.finder_result_3") },
        { key: "average", label: t("finder_result.finder_result_4") },
        { key: "average_p90", label: t("finder_result.finder_result_5") },
        { key: "hit_rate", label: t("finder_result.finder_result_6") },
        { key: "form", label: t("finder_result.finder_result_7") }
    ];

    const [selectedSort, setSelectedSort] = useState<keyof PlayerResult>('total');
    const [descSort, setDescSort] = useState<boolean>(true);
    const [tableSort, setTableSort] = useState<PlayerResult[]>([]);

    const [page, setPage] = useState(1);
    const rowsPerPage = isMobile() ? 11 : 15;
    const totalPages = Math.ceil(tableSort.length / rowsPerPage);

    const cellResultsByKey = (player: PlayerResult, key: string | number) => {
        if (key == 'player_name') return (
            <Link to={`/player/${player.player_id}`}>
                <div className="flex items-center">
                    <PlayerLogo playerId={player.player_id} size={30} className='ms-1 me-2' />
                    <div className="flex flex-col">
                        <span className={`${isMobile() ? 'text-xs' : 'text-sm'} text-default-800 text-left flex pl-1 font-medium`}>{player.player_name}</span>
                        <div className="flex items-center min-w-[130px] gap-1">
                            <div className='flex items-center'>
                                <TeamLogo className='mx-1' teamId={player.team_id} size={10} />
                            </div>
                            <span className={`${isMobile() ? 'text-[10px]' : 'text-xs'} text-default-500`}>{player.team_name}</span>
                        </div>
                    </div>
                </div>
            </Link >
        )
        if (key == 'next_team') return (
            <Link className="flex items-center justify-center" to={`/game/${player.next_fixture_id}`}>
                <TeamLogo className='mx-1' teamId={player.opponent_id} size={24} />
            </Link>
        )
        if (key == 'hit_rate') return (
            <span className={`text-default-700 font-semibold`}>{`${Number(player[key]) * 100}%`}</span>
        )
        if (key == 'form') return (
            player[key].map((stat: number, i: number) => (
                <span key={`${player.player_id}-form-${i}`} style={{ fontFamily: '"Inter", sans-serif, "Courier", "Courier New", monospace' }} className={`font-semibold pr-1 text-xs ${Number(stat) >= Number(atLeast) ? 'text-green-600' : 'text-red-500'}`
                } > {`${Number(stat)}`}</span >
            ))
        )
        return <span className={`text-default-700 font-semibold`}>{formatNumber(player[key as keyof PlayerResult])}</span>;
    }

    const filterStatsClass = 'flex rounded-large text-xs font-normal text-default-600 border-2 border-primary-500 px-2 py-[6px]';
    const isSortColunm = (columnKey: string) => ['player_name', 'total', 'average', 'average_p90', 'hit_rate'].includes(columnKey);

    const setSorted = (sortKey: any) => {
        if (isSortColunm(sortKey)) {
            if (sortKey == selectedSort) setDescSort(!descSort);
            else setSelectedSort(sortKey);
        }
    }

    useEffect(() => {
        const sortedTable = [...tableResult].sort((a, b) => {
            if (selectedSort === "player_name") {
                return descSort
                    ? a.player_name.localeCompare(b.player_name)
                    : b.player_name.localeCompare(a.player_name);
            } else {
                const valueA = a[selectedSort] as number;
                const valueB = b[selectedSort] as number;
                return descSort ? valueB - valueA : valueA - valueB;
            }
        });
        setTableSort(sortedTable);
    }, [selectedSort, descSort, tableResult]);

    const paginatedPlayers = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return tableSort.slice(start, start + rowsPerPage);
    }, [page, tableSort]);

    return (
        <div className="flex w-full flex-col pb-5 gap-4 rounded-large">
            {loading ? <div className="flex w-full h-full items-center justify-center"><Spinner /></div> : (tableResult.length ?
                <div className="flex flex-col gap-2">
                    <div className="flex gap-2 flex-wrap items-center">
                        <span className={filterStatsClass}>{`${atLeast}+ ${statName}`}</span>
                        <span className={filterStatsClass}>{t('finder_result.finder_result_8', { lastGames })}</span>
                        <span className={filterStatsClass}>{t('finder_result.finder_result_9', { hitRate: hitRate * 100 })}</span>
                        {leaguesNames && (
                            <Tooltip content={leaguesNames.map((l, i) => `${l}${leaguesNames.length - 1 == i ? '.' : ', '}`)}>
                                <span className={filterStatsClass}>
                                    {leaguesNames.length > 4
                                        ? t('finder_result.finder_result_10', { leagues: leaguesNames.slice(0, 4).join(', '), more: leaguesNames.length - 4 })
                                        : leaguesNames.map(l => l).join(', ')
                                    }
                                </span>
                            </Tooltip>
                        )}
                    </div>
                    <Table
                        className="fadeIn"
                        classNames={{ wrapper: `p-0 rounded-medium shadow-large max-w-[1040px] overflow-y-hidden` }}
                        aria-label="table-player-result"
                        bottomContent={
                            <div className="flex w-full justify-center mt-3 mb-7">
                                <Pagination
                                    showControls
                                    total={totalPages}
                                    page={page}
                                    onChange={setPage}
                                />
                            </div>
                        }
                    >
                        <TableHeader columns={columns}>
                            {(column) => <TableColumn align={['name', 'form'].includes(column.key) ? 'start' : 'center'} key={column.key}>
                                <div onClick={() => setSorted(column.key)} className={`flex items-center w-full gap-2 hover:text-default-700 ${['next_team', 'total', 'average', 'hit_rate'].includes(column.key) && 'justify-center'} ${selectedSort == column.key && 'text-default-900'} ${isSortColunm(column.key) && 'cursor-pointer'}`}>
                                    <span>{column.label}</span>
                                    {isSortColunm(column.key) && <Icon className={`text-lg transition ${selectedSort == column.key && !descSort && 'rotate-180'}`} icon="material-symbols:keyboard-arrow-down" />}
                                </div>
                            </TableColumn>}
                        </TableHeader>
                        <TableBody>
                            {paginatedPlayers.map((item: PlayerResult, i) => (
                                <TableRow className={`hover:bg-default-200 transition ${i % 2 != 0 && 'bg-default-100'}`} key={item.player_id}>
                                    {(columnKey) => <TableCell>{cellResultsByKey(item, columnKey)}</TableCell>}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                : (seeResult && <div className="flex w-full h-full gap-2 items-center justify-center fadeIn">
                    <Icon className="text-3xl text-default-400" icon="material-symbols:speaker-notes-off-outline" />
                    <span className="text-lg text-default-500">{t('finder_result.finder_result_11')}</span>
                </div>)
            )}
        </div>
    );
};

export default FinderResult;
